'use client';

import { classes } from '@/uiPrimitives/base/classes.helpers';
import { indentSize } from '@/uiPrimitives/layout/indentSize.definitions';
import { occupy } from '@/uiPrimitives/layout/occupy';
import { shelf } from '@/uiPrimitives/layout/shelf';
import { RadioField } from '../ContactForm.types';
import { radioCSS } from '@/uiComposites/form/radio.css';
import { textBlock } from '@/uiPrimitives/typography/text';
import { FieldIssue } from '../FieldIssue';
import { box } from '@/uiPrimitives/layout/box';
import { useInputProps } from '../helpers/useInputProps';
import { useFieldState } from '../helpers/useFieldState';
import { stack } from '@/uiPrimitives/layout/stack';
import { FieldLabel } from '../FieldLabel';
import { arrange } from '@/uiPrimitives/layout/arrange';
import { hidden } from '@/uiPrimitives/utility/display.css';

export const RadioFormField = ({
	id,
	...field
}: RadioField & { id: string }) => {
	const {
		name,
		label,
		required,
		description,
		options,
		nothingChosenErrorMessage = '',
	} = field;
	const inputProps = useInputProps(field);
	const { isDirty, isTouched, isValid, issue, value } = useFieldState(name);

	const showEmptyError =
		isDirty && isTouched && !isValid && issue === nothingChosenErrorMessage;
	const emptyErrorId = `error-${name}-empty`;

	return (
		<div
			id={`form:${name}`}
			className={classes(stack({ rowGap: '2 | h2' }))}
			role="radiogroup"
			aria-labelledby={`label-${id}`}
		>
			<FieldLabel
				id={`label-${id}`}
				htmlFor={undefined}
				label={label}
				required={required}
			/>
			<div
				className={classes(
					stack({ rowGap: showEmptyError ? '2 | h2' : undefined }),
				)}
			>
				<div
					className={classes(
						arrange({
							layout: { max640px: 'stack', min640px: 'shelf' },
							gridTemplateColumns:
								description != null
									? 'minmax(0, 1fr) minmax(0, 1fr)'
									: 'minmax(0, 1fr) auto',
							gap: '1 | h3',
						}),
					)}
				>
					{options != null && (
						<div className={classes(shelf({ gap: '4 | betweenListItems' }))}>
							{options.map((option, index) => {
								return (
									<div key={index} className={classes(shelf({}))}>
										<label
											htmlFor={`${id}-option-${index}`}
											className={classes(shelf({}))}
										>
											<span
												className={classes(
													occupy({
														blockSize: 'capSize',
														inlineSize: indentSize,
														justifyItems: 'start',
													}),
												)}
											>
												<input
													type="radio"
													aria-describedby={
														showEmptyError ? emptyErrorId : undefined
													}
													name={name}
													id={`${id}-option-${index}`}
													value={option.value}
													checked={option.value === value}
													className={classes(
														radioCSS,
														showEmptyError &&
															box({ borderColor: 'positiveRed' }),
													)}
													{...inputProps}
												/>
											</span>
											<span className={classes(textBlock({}))}>
												{option.label}
											</span>
										</label>
									</div>
								);
							})}
						</div>
					)}
					{description != null && (
						<div
							className={classes(
								textBlock({
									capSize: '-1 | small',
									fontStyle: 'italic',
								}),
							)}
						>
							{description}
						</div>
					)}
				</div>
				<FieldIssue
					id={emptyErrorId}
					hiddenClassName={!showEmptyError && hidden}
				>
					{nothingChosenErrorMessage}
				</FieldIssue>
			</div>
		</div>
	);
};
